import React, { useState } from "react";
import { FaCheck } from "react-icons/fa6";
import { GrAchievement } from "react-icons/gr";
import { faqDataNL, programDetails, reviews } from "../data/data";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import PublicHero from "../components/PublicHero";
import OfferCards from "../components/offers/OfferCards";
import SubNav, { defensieSubNavItems } from "../components/SubNav";
import ProgramPreviewCarousel from "../components/program/ProgramPreviewCarousel";
import PerformanceBufferSection from "../components/preparation/PerformanceBufferSection";

const programExperienceCards = reviews.slice(1, 3).map((review) => ({
  ...review,
  trajectory: "Persoonlijke begeleiding",
}));

const methodSteps = [
  {
    title: "Startniveau eerst",
    text: "Iedere voorbereiding begint met waar jij nu staat. We kijken naar je huidige kracht, conditie, mobiliteit, herstel, trainingsachtergrond en eventuele zwakke schakels.",
  },
  {
    title: "Trainen richting functie-eisen",
    text: "Je voorbereiding wordt gekoppeld aan de richting waar je naartoe werkt. Korps Mariniers, Korps Commandotroepen, 11 Luchtmobiele Brigade of andere militaire trajecten vragen ieder andere accenten.",
  },
  {
    title: "Belastbaarheid opbouwen",
    text: "Je bouwt niet alleen conditie of kracht op, maar ook het vermogen om training, vermoeidheid, volume en herhaling beter te verdragen.",
  },
  {
    title: "Progressie bewaken",
    text: "De opbouw volgt een duidelijke lijn. Trainingsbelasting, herstel en uitvoering worden afgestemd zodat je sterker wordt zonder onnodige ruis.",
  },
  {
    title: "Schalen & evalueren",
    text: "In het zelfstandige programma schaal je zelf op basis van uitvoering, herstel en belastbaarheid. Bij persoonlijke begeleiding kijken we actief mee en sturen we bij waar nodig.",
  },
];

const programBuildItems = [
  {
    title: "Belastbaarheid & rucking",
    text: "Je leert belasting geleidelijk dragen en opbouwen, zodat marsen, vermoeidheid en herhaling minder snel je zwakke schakel worden.",
  },
  {
    title: "Hardlopen & interval",
    text: "Niet alleen tempo, maar ook basisconditie, herstelvermogen en het vermogen om onder vermoeidheid te blijven bewegen.",
  },
  {
    title: "Kracht & calisthenics",
    text: "Trekkracht, duwkracht, rompcontrole en onderlichaam worden gekoppeld aan uitvoering, herhaling en belastbaarheid.",
  },
  {
    title: "Zwemmen & watervertrouwen",
    text: "Voor kandidaten waarbij wateronderdelen relevant zijn, blijft zwemvaardigheid onderdeel van de bredere voorbereiding.",
  },
  {
    title: "Herstel & bijsturen",
    text: "Je leert signalen herkennen, belasting aanpassen en consistent blijven zonder onnodig tegen je grens te trainen.",
  },
  {
    title: "Mentale tolerantie",
    text: "Structuur, discipline en omgaan met ongemak worden niet los gezien van de fysieke opbouw.",
  },
];

const entryBaseItems = [
  "5 strikte pull-ups",
  "15 nette push-ups achter elkaar",
  "5 km hardlopen onder 28 minuten",
  "4 km pijnvrij wandelen met lichte rugzak rond 5 kg",
  "Geen actieve schouder-, knie-, rug- of heupklachten tijdens bewegen",
];

const scaleAdviceItems = [
  "Je kunt het programma zelfstandig schalen",
  "Halveer loopafstanden waar nodig",
  "Start marsen met lichte belasting",
  "Houd krachttraining rond RPE 6",
  "Houd calisthenics rond RPE 6 met ruime herhalingen over",
  "Bouw eerst 3 tot 4 weken op voordat je volledig instapt",
  "Kies begeleiding als je zekerheid wilt over schalen, klachten of opbouw",
];

const programServiceSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "Mission Movement Voorbereidingsprogramma",
  url: "https://missionmovement.nl/program",
  provider: {
    "@type": "Organization",
    name: "Mission Movement",
    url: "https://missionmovement.nl/",
  },
  areaServed: {
    "@type": "Country",
    name: "Netherlands",
  },
  serviceType: "Militaire voorbereiding en begeleiding richting Defensie",
  audience: {
    "@type": "Audience",
    audienceType:
      "Kandidaten richting Defensie, Korps Mariniers, Korps Commandotroepen, 11 Luchtmobiele Brigade en specialistische militaire trajecten",
  },
  description:
    "Het Mission Movement programma helpt kandidaten gericht bouwen aan fysieke voorbereiding, belastbaarheid, discipline en mentale voorbereiding richting Defensie, selectie, opleiding en de fysieke belasting daarna.",
};

const Program = () => {
  const [openProgramIndex, setOpenProgramIndex] = useState(null);
  const [openFAQIndex, setOpenFAQIndex] = useState(null);

  const toggleProgram = (index) => {
    setOpenProgramIndex(openProgramIndex === index ? null : index);
  };

  const coreValues = [
    "Discipline",
    "Broederschap",
    "Doelgerichtheid",
    "Eer",
    "Structuur",
  ];

  return (
    <div>
      <Helmet>
        <title>Programma | Militair Voorbereidingsprogramma Defensie</title>
        <meta
          name="description"
          content="Train gericht met Mission Movement en bouw aan fysieke voorbereiding, belastbaarheid, discipline en mentale weerbaarheid richting Defensie, selectie, opleiding en de fysieke belasting daarna."
        />
        <meta
          property="og:title"
          content="Programma | Militair Voorbereidingsprogramma Defensie"
        />
        <meta
          property="og:description"
          content="Train gericht met Mission Movement en bouw aan fysieke voorbereiding, belastbaarheid, discipline en mentale weerbaarheid richting Defensie, selectie, opleiding en de fysieke belasting daarna."
        />
        <meta
          property="og:image"
          content="https://missionmovement.nl/img/milprep.png"
        />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:url" content="https://missionmovement.nl/program" />
        <link rel="canonical" href="https://missionmovement.nl/program" />
        <script type="application/ld+json">
          {JSON.stringify(programServiceSchema)}
        </script>
      </Helmet>

      <PublicHero
        eyebrow="Defensie-programma"
        title={
          <>
            <span className="md:hidden">
              Militair voorbereidings-
              <br />
              programma
            </span>
            <span className="hidden md:inline">
              Militair Voorbereidingsprogramma
            </span>
          </>
        }
        subtitle="Een 12-weekse militaire voorbereiding waarin doelgerichte oefenselectie, progressieve belasting, intensiteitsbewaking en herstel samenkomen. Elke week heeft een functie: van technische basis en belastbaarheid tot taakgerichte capaciteit en testmomenten."
        mobileSubtitle="Een 12-weekse voorbereiding voor kandidaten die niet alleen de eis willen halen, maar met een sterkere basis willen starten."
        button={{
          label: "Bekijk hoe het werkt",
          to: "/program#programma-aanpak",
        }}
        imageSrc="/img/mast2.png"
        imageAlt="Militaire voorbereiding voor Defensie"
        imageClassName="object-[middle] md:object-top"
        compact
      />

      <div className="relative shadow-sm">
        <SubNav variant="dark" scrollable items={defensieSubNavItems} />
      </div>

      <section className="bg-mmSurface py-4 border-t border-b border-mmBorder w-full text-center">
        <div className="text-mmTextMuted font-display tracking-widest uppercase text-md md:text-lg flex flex-wrap justify-center gap-4 px-4">
          {coreValues.map((value, index) => (
            <React.Fragment key={index}>
              <span className="px-1 hover:text-mmAccent transition duration-200">
                {value}
              </span>

              {index < coreValues.length - 1 && (
                <span className="text-mmAccent">|</span>
              )}
            </React.Fragment>
          ))}
        </div>
      </section>

      <section
        id="programma-aanpak"
        className="w-full px-5 py-14 sm:px-10 md:py-16 lg:px-20"
      >
        <div className="mx-auto w-full max-w-7xl tracking-wider text-mmText">
          <div className="mm-card p-5 md:p-7 lg:p-8">
            <h2 className="mm-h2 border-b border-mmBorder pb-2 mb-4 text-mmText">
              De opbouw achter het programma
            </h2>
            <p className="text-sm md:text-base font-normal text-mmTextMuted text-left leading-relaxed mb-8 max-w-5xl mx-auto">
              Het Militair Voorbereidingsprogramma van Mission Movement is een
              12-weekse voorbereidingsstructuur voor wie zich serieus wil
              voorbereiden op Defensie, selectie, opleiding en de fysieke
              belasting daarna.
              <br />
              <br />
              Een goede voorbereiding begint bij weten wat er ontwikkeld moet
              worden. Daarom werkt Mission Movement vanuit jouw startniveau,
              jouw doelrichting en de fysieke eisen die straks op je afkomen. Je
              traint niet alleen voor de test. Je bouwt aan het lichaam dat de
              opleiding en het werk daarna moet kunnen dragen.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-3">
              {methodSteps.map((step, index) => (
                <article
                  key={step.title}
                  className="bg-mmSurface border border-mmBorder rounded-xl p-4"
                >
                  <div className="h-8 w-8 rounded-full border border-mmBorderStrong flex items-center justify-center text-mmAccent font-semibold mb-3">
                    {index + 1}
                  </div>
                  <h2 className="font-display uppercase tracking-widest text-mmAccent text-sm mb-2">
                    {step.title}
                  </h2>
                  <p className="text-mmTextMuted text-xs leading-relaxed">
                    {step.text}
                  </p>
                </article>
              ))}
            </div>
          </div>

          <PerformanceBufferSection className="mt-10" />

          <div className="mm-card p-5 md:p-7 lg:p-8 mt-10">
            <div className="max-w-4xl">
              <p className="font-display uppercase tracking-[0.18em] text-mmAccent text-sm md:text-base mb-3">
                Wat je opbouwt
              </p>
              <h2 className="mm-h2 text-mmText mb-4">
                Breder dan testtraining
              </h2>
              <p className="text-sm md:text-base text-mmTextMuted leading-relaxed">
                Het programma werkt aan de fysieke basis die straks meer moet
                dragen dan een losse testscore. Je traint richting meer marge,
                minder werken op je absolute grens en beter kunnen blijven
                bewegen onder belasting.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 mt-7">
              {programBuildItems.map((item) => (
                <article
                  key={item.title}
                  className="bg-mmSurface border border-mmBorder rounded-xl p-5"
                >
                  <div className="h-8 w-8 rounded-full border border-mmBorderStrong flex items-center justify-center text-mmAccent mb-4">
                    <GrAchievement className="h-4 w-4" />
                  </div>
                  <h3 className="font-display uppercase tracking-widest text-mmText text-sm md:text-base mb-3">
                    {item.title}
                  </h3>
                  <p className="text-sm text-mmTextMuted leading-relaxed">
                    {item.text}
                  </p>
                </article>
              ))}
            </div>
          </div>

          <div className="mm-card p-5 md:p-7 lg:p-8 mt-10">
            <h2 className="text-mmAccent font-display text-xl md:text-2xl tracking-widest uppercase mb-3">
              In het militaire handboek
            </h2>
            <p className="text-sm md:text-base text-mmTextMuted leading-relaxed mb-6">
              Het militaire handboek hoort bij de Defensie-route. Het geeft je
              de basis, structuur en onderdelen om gericht te werken aan je
              voorbereiding, zelfstandig of als onderdeel van begeleiding
              richting Defensie.
            </p>

            {programDetails.map((item, index) => (
              <div
                key={item.title}
                className="mb-3 bg-mmSurface border border-mmBorder rounded-xl overflow-hidden transition-all duration-300 hover:border-mmAccent/40"
              >
                <button
                  onClick={() => toggleProgram(index)}
                  className="w-full flex justify-between items-center gap-4 px-4 py-4 text-left text-mmText text-xs md:text-sm font-medium uppercase bg-mmSurface hover:text-mmAccent transition duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-mmAccent/40"
                  aria-expanded={openProgramIndex === index}
                >
                  <span>{item.title}</span>
                  <span className="text-mmAccent text-xl shrink-0">
                    {openProgramIndex === index ? "−" : "+"}
                  </span>
                </button>
                <div
                  className={`overflow-hidden transition-all duration-500 ${
                    openProgramIndex === index
                      ? "max-h-[240px] opacity-100 px-4 pb-4"
                      : "max-h-0 opacity-0"
                  }`}
                >
                  <p className="text-mmTextMuted text-sm leading-relaxed">
                    {item.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <ProgramPreviewCarousel />

      <section
        id="routeadvies"
        className="w-full bg-mmPage px-5 py-14 sm:px-10 md:py-16 lg:px-20 border-t border-mmBorder"
      >
        <div className="mx-auto w-full max-w-7xl">
          <div className="mm-card p-5 md:p-7 lg:p-8">
            <p className="font-display uppercase tracking-[0.18em] text-mmAccent text-sm md:text-base mb-3">
              Routeadvies
            </p>
            <h2 className="mm-h2 text-mmText mb-4">
              Voor wie is het zelfstandige programma geschikt?
            </h2>
            <div className="space-y-4 text-sm md:text-base text-mmTextMuted leading-relaxed max-w-5xl">
              <p>
                Het zelfstandige programma is gebouwd voor kandidaten met een
                redelijke fysieke basis. Week 1 is bewust niet eenvoudig. Zit je
                onder de onderstaande richtlijn, dan betekent dat niet dat je
                niets kunt doen. In het programma krijg je instructies om
                onderdelen te schalen, afstanden te verlagen en de belasting
                gecontroleerd op te bouwen.
              </p>
              <p>
                Ben je ver onder deze basis, heb je pijnklachten of twijfel je
                hoe je moet schalen, dan is het begeleide traject verstandiger.
                Dan starten we precies waar jij nu staat, bouwen we de belasting
                gecontroleerd op en zorgen we dat je niet harder traint dan je
                lichaam op dat moment kan verwerken.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-7">
              <article className="bg-mmSurface border border-mmBorder rounded-xl p-5">
                <h3 className="font-display uppercase tracking-widest text-mmText text-sm md:text-base mb-4">
                  Minimale basis om zelfstandig te starten
                </h3>
                <ul className="space-y-2 text-sm text-mmTextMuted">
                  {entryBaseItems.map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <FaCheck className="text-mmAccent min-w-[16px] h-[16px] mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-mmTextMuted text-xs md:text-sm leading-relaxed mt-4 border-l-2 border-mmAccent pl-3">
                  Dit zijn geen officiële selectie-eisen. Dit zijn Mission
                  Movement instroomrichtlijnen om het zelfstandige programma
                  verantwoord te starten.
                </p>
              </article>

              <article className="bg-mmSurface border border-mmBorder rounded-xl p-5">
                <h3 className="font-display uppercase tracking-widest text-mmText text-sm md:text-base mb-4">
                  Zit je hieronder?
                </h3>
                <ul className="space-y-2 text-sm text-mmTextMuted">
                  {scaleAdviceItems.map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <FaCheck className="text-mmAccent min-w-[16px] h-[16px] mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </article>
            </div>
          </div>

          <div className="mm-card p-5 md:p-7 lg:p-8 mt-10">
            <h2 className="text-mmAccent font-display text-xl md:text-2xl tracking-widest uppercase mb-3">
              Twee manieren om te starten
            </h2>
            <p className="text-sm md:text-base text-mmTextMuted leading-relaxed mb-6 max-w-5xl">
              Zelfstandig is logisch als je al een basis hebt, zelf kunt
              trainen en bereid bent onderdelen te schalen waar nodig.
              Begeleiding is verstandiger als je nog moet opbouwen, klachten
              hebt of niet zeker weet hoe je verantwoord moet schalen.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-7">
              <article className="rounded-xl border border-mmBorder bg-black/20 p-5">
                <h3 className="font-display uppercase tracking-widest text-mmText text-sm md:text-base mb-2">
                  Zelfstandig
                </h3>
                <p className="text-sm text-mmTextMuted leading-relaxed">
                  Ik heb een basis en wil zelfstandig starten.
                </p>
              </article>
              <article className="rounded-xl border border-mmBorder bg-black/20 p-5">
                <h3 className="font-display uppercase tracking-widest text-mmText text-sm md:text-base mb-2">
                  Begeleid
                </h3>
                <p className="text-sm text-mmTextMuted leading-relaxed">
                  Ik wil begeleiding bij mijn opbouw.
                </p>
              </article>
            </div>

            <OfferCards variant="defensie" context="embedded" />

            <p className="text-mmTextMuted text-xs md:text-sm leading-relaxed mt-5 border-l-2 border-mmAccent pl-4">
              Beide routes horen bij de Defensie-route. Het verschil zit in de
              mate van begeleiding: zelfstandig werken met de structuur, of
              persoonlijk begeleid worden op basis van jouw startniveau,
              uitvoering, herstel en voortgang.
            </p>
          </div>
        </div>
      </section>

      <section className="w-full bg-mmPage py-16 md:py-20 px-5 sm:px-10 lg:px-20 border-t border-mmBorder">
        <div className="max-w-5xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-10">
            <p className="font-display uppercase tracking-[0.18em] text-mmAccent text-sm md:text-base mb-3">
              Ervaringen
            </p>
            <h2 className="mm-h2 text-mmText mb-4">Ervaringen van anderen</h2>
            <p className="text-mmTextMuted text-base md:text-lg leading-relaxed">
              Lees hoe anderen begeleiding, structuur en voorbereiding hebben
              ervaren via Mission Movement.
            </p>
          </div>

          <div className="grid gap-5 md:grid-cols-2">
            {programExperienceCards.map((review) => (
              <article key={review.name} className="mm-card p-6 h-full">
                <div className="mb-5">
                  <h3 className="font-display uppercase tracking-widest text-mmText text-lg">
                    {review.name}
                  </h3>
                  <p className="text-mmAccent text-xs uppercase tracking-widest mt-1">
                    {review.trajectory}
                  </p>
                </div>
                <p className="text-mmTextMuted text-base leading-relaxed">
                  {review.text}
                </p>
              </article>
            ))}
          </div>

          <div className="mt-8 flex justify-center">
            <Link to="/ervaringen" className="mm-btn">
              Bekijk ervaringen
            </Link>
          </div>
        </div>
      </section>

      <section className="w-full bg-mmPage py-16 md:py-20 px-5 sm:px-10 lg:px-20 border-t border-mmBorder">
        <div className="max-w-5xl mx-auto">
          <h2 className="mm-h2 text-mmText mb-6">Veelgestelde Vragen</h2>

          <div className="space-y-4">
            {faqDataNL.map((faq, index) => {
              const isOpen = openFAQIndex === index;
              return (
                <div
                  key={index}
                  className={`border border-mmBorder rounded-2xl transition-all duration-300 ${
                    isOpen ? "bg-mmSurface border-mmAccent" : "bg-mmSurface"
                  }`}
                >
                  <button
                    onClick={() => setOpenFAQIndex(isOpen ? null : index)}
                    className="w-full flex justify-between items-center gap-4 px-6 py-4 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-mmAccent/40"
                    aria-expanded={isOpen}
                  >
                    <span className="text-mmAccent font-display text-base md:text-lg tracking-widest uppercase">
                      {faq.vraag}
                    </span>
                    <span className="text-mmAccent text-xl">
                      {isOpen ? "−" : "+"}
                    </span>
                  </button>
                  <div
                    className={`overflow-hidden transition-all duration-500 ease-in-out ${
                      isOpen ? "max-h-[1000px] py-4 px-6" : "max-h-0"
                    }`}
                  >
                    <p className="whitespace-pre-line text-mmTextMuted leading-relaxed text-base md:text-lg">
                      {faq.antwoord}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="w-full bg-mmPage py-16 md:py-20 px-5 sm:px-10 lg:px-20 border-t border-mmBorder">
        <div className="mm-card max-w-4xl mx-auto p-7 md:p-10 text-center">
          <p className="font-display text-mmAccent text-sm md:text-base uppercase tracking-widest mb-3">
            Mission Movement
          </p>
          <h2 className="mm-h2 mb-4">Klaar om gericht te starten?</h2>
          <p className="text-mmTextMuted text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
            Kies of je zelfstandig met het 12-weekse programma begint, of start
            met persoonlijke begeleiding rond jouw doel, startniveau en
            voorbereiding.
          </p>
          <Link to="/pricing" className="inline-flex mt-8">
            <button className="mm-btnPrimary px-8 py-4 text-center">
              Kies je voorbereiding
            </button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Program;
